import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { PaginatedPost } from "../../log.typing";

const SETTLE_POLL_MS = 50;

const isOlderPage = (next: PaginatedPost[], shown: PaginatedPost[]) =>
  shown.length > 0 &&
  next.length > 0 &&
  next[0].PostId !== shown[0].PostId &&
  next.some((post) => post.PostId === shown[0].PostId);

/**
 * Decides which posts are on screen. An older page that arrives while the reader is scrolling is held
 * back until they stop: on phones a fling runs outside the page, and posts added above the reader
 * mid-fling carried them into the new page. Every other change is shown straight away.
 *
 * `isPrepending` is true for the render that puts a page of older posts on screen.
 */
export const useHeldOlderPage = (
  posts: PaginatedPost[],
  isReaderScrolling: () => boolean
) => {
  const shownRef = useRef(posts);
  const [, recheck] = useState(0);

  const olderPageArrived = isOlderPage(posts, shownRef.current);
  const isHeld = olderPageArrived && isReaderScrolling();
  const shownPosts = isHeld ? shownRef.current : posts;

  useLayoutEffect(() => {
    shownRef.current = shownPosts;
  });

  // Nothing re-renders the timeline when the reader stops, so a held page polls for it.
  useEffect(() => {
    if (!isHeld) return undefined;
    const timer = window.setInterval(() => {
      if (!isReaderScrolling()) recheck((count) => count + 1);
    }, SETTLE_POLL_MS);
    return () => window.clearInterval(timer);
  }, [isHeld, isReaderScrolling]);

  return {
    shownPosts,
    isHeld,
    isPrepending: olderPageArrived && !isHeld,
  };
};
